<template>
  <div class="" v-if="isUserLoading || isPropertyLoading">
    <Loading />
  </div>
  <div v-else-if="loginError">
    <div>
      <h1>Oops, an error occured while logging you in.</h1>
      <h3>Please check your information and try again.</h3>
      <div>
        {{ loginError }}
      </div>
    </div>
  </div>

  <div v-else>
    <BethsLayout>
      <div class="flex flex-col min-h-screen">
        <div class="w-12/12 sm:w-10/12 m-auto my-10 bg-white shadow-md lg:w-6/12 md:6/12">
          <div class="px-2 xs:px-5 py-8 rounded-xl">
            <h1 aria-label="title" class="mt-3 text-2xl font-roboto text-center">
              Finish your registration at<br />
              {{ property?.name }}
            </h1>
            <h2 class="mt-8 font-roboto text-base">
              It looks like this is your first time visiting this building.<br />
              Please verify this info is correct and then click continue below.
            </h2>
            <ErrorDisplay
              label="Authenication Error"
              :message="errorString"
              :isVisible="errorString != null"
            />

            <div class="mt-8 w-full">
              <div class="py-2">Logging in as:</div>
              <div v-if="user && user.loginEmail" class="py-2 px-4 flex-1 font-mono font-bold">
                {{ user.loginEmail }}
              </div>
            </div>

            <form class="mt-2">
              <div class="role-input">
                <div class="flex-1 my-5 ml-0">
                  <label for="roles" class="block text-black">Building Role</label>

                  <div class="relative" data-cy="required">
                    <select
                      id="roles"
                      v-model="role"
                      class="block w-full px-4 py-3 pr-8 mt-3 ml-2 bg-gray-100 appearance-none focus:outline-none bg-grey-lighter text-grey-darker"
                    >
                      <option
                        v-for="roleObj in roles"
                        :key="roleObj.siteRoleId"
                        :value="roleObj.siteRoleId"
                      >
                        {{ roleObj.roleAlias }}
                      </option>
                    </select>
                    <div
                      class="absolute flex items-center px-2 pointer-events-none right-3 top-3 text-grey-darker"
                    >
                      <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                        <path
                          d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                        />
                      </svg>
                    </div>
                    <div v-if="errors && errors.role" class="text-red-800">
                      Please select a role
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="property.propertyType != PropertyType.SingleTenant" class="">
                <div class="flex-1 my-5 text-sm">
                  <label for="tenant" class="block text-black">
                    {{ property.tenantAlias ? property.tenantAlias : "Tenant" }}
                  </label>

                  <div class="relative">
                    <select
                      id="tenant"
                      v-model="tenant"
                      class="w-full px-4 py-3 text-xl font-mono appearance-none bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                    >
                      <option
                        v-for="propTenant in tenants"
                        :key="propTenant.tenantId"
                        :value="propTenant.tenantId"
                      >
                        {{ propTenant.name }}
                      </option>
                    </select>
                    <div
                      class="absolute flex items-center px-2 pointer-events-none right-3 top-3 text-grey-darker"
                    >
                      <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                        <path
                          d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                        />
                      </svg>
                    </div>
                    <div v-if="errors && errors.tenant" class="text-red-800">
                      Please select your Tenant
                    </div>
                  </div>
                </div>
              </div>

              <div class="my-5 text-sm">
                <BaseSelect
                  v-if="property.floorList?.length > 0"
                  label="Primary Floor"
                  v-model="primaryFloor"
                  :options="floorList"
                  class="w-full px-4 py-3 text-xl font-mono appearance-none bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                />
                <BaseInput
                  v-else
                  class="w-full px-4 text-base font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                  label="Primary Floor"
                  v-model="primaryFloor"
                />
              </div>

              <div class="mt-4" v-if="property.isMobilityCheckEnabled ?? true">
                <BaseCheckbox
                  v-model="mobilityImpaired"
                  label="I have a Mobility Impairment"
                  :error="errors.mobilityImpaired"
                ></BaseCheckbox>
              </div>

              <div v-if="property?.isAlertEnabled" class="mb-8 mt-8">
                <hr class="mb-4" />
                <p class="text-black text-lg mb-2">Emergency Contact Info</p>
                <p class="text-sm mb-6">
                  If you would like to receive Safetynet notifications about emergencies affecting
                  this building, enter an email address and/or SMS-enabled cell phone number.
                </p>
                <p></p>
                <div class="">
                  <div class="mr-2 text-sm w-12/12 md:w-1/2">
                    <BaseInput
                      v-model="alertPhone"
                      label="Emergency Alert SMS"
                      placeholder="Cell# to text Alerts"
                      type="text"
                      class="w-full px-4 text-base font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                      autocomplete="tel"
                      :error="errors.alertPhone"
                    />
                  </div>
                  <div class="text-sm w-12/12 md:w-1/2">
                    <BaseInput
                      v-model="alertEmail"
                      label="Emergency Alert Email"
                      placeholder="Email to send alerts"
                      type="text"
                      class="w-full px-4 text-base font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                      autocomplete="email"
                      :error="errors.alertEmail"
                    />
                  </div>
                </div>

                <div>
                  <p class="mt-6 mb-3 text-sm">Select floors that you want notifications for:</p>
                  <div class="flex">
                    <BaseCheckbox class="grow mt-1" v-model="selectAllFloors" label="All Floors" />
                    <div v-if="!selectAllFloors">
                      <button
                        v-if="primaryFloor.length > 0"
                        class="p-1 mr-2 bg-white border border-gray-500 rounded-sm text-sm"
                        type="button"
                        @click="
                          () => {
                            if (!selectAllFloors && !selectedFloors.includes(primaryFloor))
                              selectedFloors.push(primaryFloor);
                          }
                        "
                      >
                        Select my floor
                      </button>
                      <button
                        class="p-1 bg-white border border-gray-500 rounded-sm text-sm"
                        type="button"
                        @click="
                          () => {
                            if (!selectAllFloors) selectedFloors = [];
                          }
                        "
                      >
                        Clear Selection
                      </button>
                    </div>
                  </div>

                  <Listbox
                    v-if="!selectAllFloors"
                    v-model="selectedFloors"
                    multiple
                    :disabled="selectAllFloors"
                  >
                    <div class="relative mt-3">
                      <ListboxButton
                        class="relative w-full py-2 pl-3 pr-10 text-left bg-white rounded-lg border shadow-md cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 sm:text-sm"
                      >
                        <span
                          :class="[selectAllFloors ? 'text-gray-500' : 'text-gray-800', 'text-md']"
                        >
                          {{
                            selectAllFloors
                              ? "All floors selected"
                              : selectedFloors.length > 0
                              ? selectedFloors.join(", ")
                              : "-- select floors --"
                          }}
                        </span>
                        <span
                          class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
                        >
                          <i-heroicons-solid-selector
                            :class="[
                              selectAllFloors ? 'text-gray-300' : 'text-gray-600',
                              'w-5 h-5 text-2xl',
                            ]"
                            aria-hidden="true"
                          />
                        </span>
                      </ListboxButton>
                      <transition
                        leave-active-class="transition duration-100 ease-in"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0"
                      >
                        <ListboxOptions
                          class="absolute w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                        >
                          <ListboxOption
                            v-for="floor in floorList"
                            :key="floor"
                            :value="floor"
                            as="template"
                          >
                            <li
                              :class="[
                                selectedFloors.includes(floor)
                                  ? 'border-1 bg-sky-300 border-blue-500'
                                  : 'hover:bg-blue-200',
                                'text-gray-900 select-none relative py-1 pl-4 pr-4 cursor-pointer',
                              ]"
                            >
                              {{ floor }}
                            </li>
                          </ListboxOption>
                        </ListboxOptions>
                      </transition>
                    </div>
                  </Listbox>
                </div>
              </div>

              <div class="mt-4">
                <BaseCheckbox
                  v-model="acceptedTerms"
                  label="I accept the <a href='terms' target='_blank' class='text-blue-500'>Terms of Use</a>*"
                  :error="errors.acceptedTerms"
                />
              </div>

              <div
                v-if="errorSavingMessage"
                class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 mt-4 rounded relative"
                role="alert"
              >
                <span class="block sm:inline mt-4 py-2">
                  Check for errors above and try again.
                </span>
              </div>
              <div v-if="isSaving" class="w-full text-center"><Loading /></div>

              <div class="pt-5 pb-28">
                <button
                  type="button"
                  class="block float-right w-full p-3 mt-5 max-w-[120px] text-center text-white duration-300 bg-gray-800 rounded-sm hover:bg-black"
                  @click="createNewAccount"
                >
                  Continue
                </button>
              </div>

              <div class="ml-8 mt-2" v-if="property.isAlertEnabled">
                <span class="text-xs mb-8 text-gray-600 italic">
                  * For SMS alerts, message and data rates may apply. You can opt out at any time by
                  removing the number from your profile or replying STOP. Contact your building
                  management for support or send email to support@akpreparedness.com.
                </span>
              </div>
            </form>
          </div>
        </div>
        <!-- {{ errors }} -->
      </div>
    </BethsLayout>
  </div>
</template>

<script setup lang="ts">
import { useBldgStore } from "../store/bldgStore";
import { useUserStore } from "../store/userStore";

import { useField, useForm } from "vee-validate";
import * as yup from "yup";

import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/vue";

import { SiteRoleDTO, PropertyType, SelfSavePersonCommand } from "../store/generatedFetchClient";
import { usePersonStore } from "../store/personStore";

const bldgStore = useBldgStore();
const userStore = useUserStore();
const personStore = usePersonStore();
const router = useRouter();
const route = useRoute();

var isPropertyLoading = computed(() => bldgStore.isLoading);
var property = computed(() => bldgStore.property);

onMounted(loadData);
watch(property, loadData);

const loginError = ref("");
const isUserLoading = ref(false);
let activatedTenantIds = ref<number[]>([]);
var returnUrl = route?.query?.returnUrl?.toString() ?? "";

async function loadData() {
  console.log("External register loaded");
  isUserLoading.value = true;

  // The SSO redirects (SamlController / AzureadController) send the JWT as
  // "authtoken". This previously read "t", which those redirects never set, so the
  // token was only ever installed by main.ts running first.
  let urlToken = route?.query?.authtoken?.toString() ?? route?.query?.t?.toString() ?? "";
  if (urlToken) {
    userStore.setAuthToken(urlToken);
    console.log("Found auth token in url params");
  }

  if (!isPropertyLoading.value && property?.value?.siteCode?.length > 0) {
    let clientName = router.currentRoute?.value?.query?.client?.toString() ?? "";

    let user1 = await userStore.getUser(
      property.value.siteCode,
      property.value.portalId,
      true,
      clientName
    );

    if (user1?.isComplete) {
      let redir = `/${property.value.siteCode}`;
      if (returnUrl) {
        redir += returnUrl;
      }
      router.replace(redir);
      return;
    }

    //just use default values and create account
    //await createNewAccount();
    isUserLoading.value = false;

    if (!user?.value?.personId) {
      loginError.value = "You don't have permission to access this building";
    }
  }
}

let user = computed(() => userStore.user);

var tenants = computed(() => {
  if (property.value && property.value.tenants) {
    let tenants = property.value.tenants;
    let unrestrictedTenants = tenants.filter(
      (t) => !t.isRestricted || activatedTenantIds.value.find((a) => a == t.tenantId)
    );
    console.log("Active Tenants", unrestrictedTenants);
    return unrestrictedTenants;
  }
  return [];
});

var floorList = computed(() => {
  let floors = bldgStore.property?.floorList;
  return floors;
});

//Populate form values at startup
watch(user, (u) => {
  if (u) {
    firstName.value = u.firstName ?? "";
    lastName.value = u.lastName ?? "";
    primaryPhone.value = u.primaryFloor ?? "";
    mobilePhone.value = u.mobilePhone ?? "";
    tenant.value = u.tenantId;
    primaryFloor.value = u.primaryFloor ?? "";
    //mobilityImpaired.value = u.
    role.value = u.siteRoleId ?? roles?.value[0].siteRoleId ?? 0;
    acceptedTerms.value = true;
    alertPhone.value = user.value.contactSms;
    alertEmail.value = user.value.contactEmail;
    selectAllFloors.value =
      user.value.alertSubscribedFloors == floorList.value.toString() ||
      user.value.alertSubscribedFloors == null;
    selectedFloors.value =
      user.value.alertSubscribedFloors != null && user.value.alertSubscribedFloors.length > 0
        ? user.value.alertSubscribedFloors.split(",")
        : [];
  }
});

let isSaving = ref(false);

var enableStaffRoles = ref(false);
var roles = computed(() => {
  if (bldgStore.property !== null) {
    let allRoles = bldgStore.property?.siteRoles ?? ([] as SiteRoleDTO[]);

    if (enableStaffRoles.value || user.value.isStaffRole) {
      console.log("User has staff role. Showing all staff roles");
      return allRoles;
    } else {
      console.log("User doesn't have staff role. Showing non-staff roles");
      return allRoles.filter((r) => !r.isStaffRole);
    }
  }
  return [];
});

var nonStaffTrainingPrograms = computed(function () {
  let bldg = bldgStore.building;
  if (bldg !== null) {
    //console.log("Training Program computed", bldgStore.property);
    let tp = bldg.trainingPrograms;
    if (tp !== null && tp !== undefined) {
      let ids = tp.map((t) => t.trainingProgramId);
      return ids;
    }
  }
  return [];
});

//See: https://github.com/jquense/yup/blob/master/docs/typescript.md
// https://vee-validate.logaretm.com/v4/api/use-form

const validationSchema = yup.object({
  firstName: yup.string().label("First Name"),
  lastName: yup.string().label("Last Name"),

  // username: yup.string().required().email().label("Username"),
  // newPassword: yup.string().required().min(5).label("Password"),

  primaryPhone: yup.string().nullable().notRequired(),
  mobilePhone: yup.string().nullable().notRequired(),

  mobilityImpaired: yup.boolean().default(false),

  //trainingProgram: yup.array<Number>().required().min(1).default([]).label("Training Program"),
  role: yup.number(), //.required(),

  tenant: yup.number().nullable().required(),
  primaryFloor: yup.string().nullable().notRequired(),

  alertPhone: yup.string().nullable().notRequired(),
  alertEmail: yup.string().nullable().notRequired().email(),

  acceptedTerms: yup.bool().default(true).oneOf([true], "Please accept Terms"),
});

const { handleSubmit, errors } = useForm({
  validationSchema,
  initialValues: {
    firstName: "",
    lastName: "",

    primaryPhone: undefined,
    mobilePhone: undefined,

    mobilityImpaired: false,

    role: undefined,

    tenant: undefined,
    primaryFloor: "",
    alertPhone: undefined,
    alertEmail: undefined,
    acceptedTerms: false,
  },
});

const { value: firstName } = useField<string>("firstName");
const { value: lastName } = useField<string>("lastName");

const { value: primaryPhone } = useField<string>("primaryPhone");
const { value: mobilePhone } = useField<string>("mobilePhone");

const { value: mobilityImpaired } = useField<boolean>("mobilityImpaired");
const { value: role } = useField<number>("role");

const { value: tenant } = useField<number>("tenant");
const { value: primaryFloor } = useField<string>("primaryFloor");

const { value: alertPhone } = useField<string>("alertPhone");
const { value: alertEmail } = useField<string>("alertEmail");

const selectAllFloors = ref(false);
const selectedFloors = ref([] as string[]);

watch(selectedFloors, () => {
  selectedFloors.value.sort((a, b) => floorList.value.indexOf(a) - floorList.value.indexOf(b));
});

const { value: acceptedTerms } = useField<boolean>("acceptedTerms");

let errorSavingMessage = ref<string | null>(null);
let infoMessage = ref<string | null>(null);

let onInvalidSubmit = function (ctx: any) {
  if (ctx?.errors) {
    console.log("error trying to submit form", ctx);
    isSaving.value = false;

    errorSavingMessage.value = Object.values(ctx.errors).join(", ");
    infoMessage.value = null;
  }
};

const errorString = ref("");

let createNewAccount = handleSubmit(async (values) => {
  try {
    console.log("Finish setting up External Account...", values);

    isSaving.value = true;
    errorSavingMessage.value = null;
    infoMessage.value = null;

    var registrationData = {
      personId: user.value.personId,
      firstName: values.firstName ?? " ",
      lastName: values.lastName,
      siteRoleId: values.role ?? roles.value.at(0),
      tenantId: values.tenant,
      primaryFloor: values.primaryFloor,
      mobilePhone: values.mobilePhone,
      primaryPhone: values.primaryPhone,
      contactEmail: values.alertEmail,
      contactSms: values.alertPhone,
      alertSubscribedFloors: selectAllFloors.value ? null : selectedFloors.value.toString(),

      portalId: property.value.portalId,
      trainingProgramIds: nonStaffTrainingPrograms.value,
    } as SelfSavePersonCommand;
    errorString.value = await personStore.selfSavePerson(registrationData);
    if (!errorString.value || errorString.value.length === 0) {
      // Successfully updated user.
      // Reload again to update logged in user
      await userStore.getUser(property.value?.siteCode, property.value.portalId, true);

      let redir = `/${property.value.siteCode}`;
      if (returnUrl) {
        redir += returnUrl;
      }
      router.replace(redir);

      router.push("/" + property.value.siteCode);
    }

    isSaving.value = false;
  } catch (error: any) {
    console.warn("Error completing account signup", error);
  }
}, onInvalidSubmit);
</script>
